import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

/**
 * The signed-in user's `trucking_drivers.id`.
 *
 * Every driver-scoped list endpoint filters on a real UUID —
 * `/trucking/loads` and `/trucking/loads/bols` both push `driver_id` straight
 * into `l.driver_id = $n` against a uuid column. Passing the literal string
 * `'me'` is therefore not a server-side convenience the API offers; it is a
 * cast error, and the screen renders its empty state on a 500. Resolve the id
 * here first and pass the resolved value.
 *
 * `/trucking/drivers/me` answers 404 for an account with no `trucking_drivers`
 * row (an office user, a casing hand), which is a legitimate outcome — the
 * caller gets `undefined` and should leave the dependent query disabled rather
 * than fetching an unfiltered list.
 */
export function useDriverId() {
  const { data, isLoading } = useQuery<string | undefined>({
    queryKey: ['trucking-driver-me-id'],
    queryFn: () =>
      api.trucking.me.profile().then((r) => {
        const body = r.data;
        const driver = body && typeof body === 'object' && 'data' in body ? body.data : body;
        return (driver as { id?: string } | null | undefined)?.id;
      }),
    retry: false,
    staleTime: 5 * 60_000,
  });

  return { driverId: data, isLoading };
}
